import React, { useEffect, useRef, useState } from 'react';
import styles from './JobQueueSetting.module.scss';
import { ReactComponent as GDV } from 'img/grip-dots-vertical.svg';
import { useAuthPage } from '@features/auth/useAuthPage';
import { getJobQueues } from '@api/jobqueues';

type JobQueueRow = {
  id: string;
  series: string;
  status: string;
  patientId: string;
  patientName: string;
  studyId: string;
  startedAt: string;
};

type JobQueueApiItem = {
  id?: string;
  added_at?: string;
  job?: {
    kind?: string;
    status?: string;
    started_at?: string;
    analysisBase?: {
      analysisStatus?: string;
      series?: Array<{
        series_description?: string;
        study?: {
          studyID?: string;
          patient?: {
            patientId?: string;
            patient_name?: string;
          };
        };
      }>;
    };
  };
};

const columns: Array<{ key: Exclude<keyof JobQueueRow, 'id'>; label: string }> = [
  { key: 'series', label: 'Series' },
  { key: 'status', label: 'Status' },
  { key: 'patientId', label: 'Patient ID' },
  { key: 'patientName', label: 'Patient Name' },
  { key: 'studyId', label: 'Study ID' },
  { key: 'startedAt', label: 'Started At' },
];

const formatStartedAt = (iso?: string): string => {
  if (!iso) {
    return '-';
  }

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const normalizePatientName = (name?: string): string =>
  name ? name.replaceAll('^', ' ') : '-';

const decodeMojibakeUtf8 = (value: string): string => {
  if (!/[ÃÂÐð]/.test(value)) {
    return value;
  }

  try {
    const bytes = Uint8Array.from(value, (char) => char.charCodeAt(0) & 0xff);
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return value;
  }
};

const normalizeText = (value?: string, fallback = '-'): string => {
  if (!value) {
    return fallback;
  }

  const decoded = decodeMojibakeUtf8(value);
  const cleaned = decoded
    .normalize('NFKC')
    .replace(/[\u0000-\u001F\u007F]/g, '')
    .trim();

  return cleaned || fallback;
};

const toJobQueueRows = (items: JobQueueApiItem[]): JobQueueRow[] =>
  items
    .filter((item) => item?.job?.kind === 'default')
    .map((item, index) => {
      const series = item?.job?.analysisBase?.series?.[0];
      const study = series?.study;
      const patient = study?.patient;

      return {
        id: item.id ?? `job-row-${index}`,
        series: normalizeText(series?.series_description, 'Series'),
        status:
          normalizeText(
            item?.job?.analysisBase?.analysisStatus ?? item?.job?.status,
            '-',
          ),
        patientId: normalizeText(patient?.patientId, '-'),
        patientName: normalizePatientName(patient?.patient_name),
        studyId: normalizeText(study?.studyID, '-'),
        startedAt: formatStartedAt(item?.job?.started_at ?? item?.added_at),
      };
    });

function moveRow<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  if (fromIndex === toIndex) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);

  return nextItems;
}

function JobQueueSetting(): React.JSX.Element {
  const [rows, setRows] = useState<JobQueueRow[]>([]);
  const [dragRowId, setDragRowId] = useState<string | null>(null);
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);
  const [shiftUpRowIds, setShiftUpRowIds] = useState<string[]>([]);
  const [shiftDownRowIds, setShiftDownRowIds] = useState<string[]>([]);

  const rowsRef = useRef<JobQueueRow[]>([]);
  const dragImageRef = useRef<HTMLDivElement | null>(null);
  const shiftResetTimerRef = useRef<number | null>(null);

  const { accessToken } = useAuthPage();

  const clearDragImage = () => {
    if (!dragImageRef.current) {
      return;
    }

    dragImageRef.current.remove();
    dragImageRef.current = null;
  };

  const createDragImage = (row: JobQueueRow): HTMLDivElement => {
    const preview = document.createElement('div');
    preview.style.position = 'fixed';
    preview.style.top = '-10000px';
    preview.style.left = '-10000px';
    preview.style.display = 'grid';
    preview.style.gridTemplateColumns = '32px repeat(6, minmax(0, 1fr))';
    preview.style.alignItems = 'center';
    preview.style.height = '32px';
    preview.style.minWidth = '760px';
    preview.style.padding = '0 8px';
    preview.style.border = '1px solid rgba(255, 255, 255, 0.16)';
    preview.style.background = 'rgba(42, 42, 42, 0.94)';
    preview.style.color = '#ffffff';
    preview.style.fontSize = '12px';
    preview.style.fontWeight = '600';
    preview.style.borderRadius = '2px';
    preview.style.boxSizing = 'border-box';
    preview.style.pointerEvents = 'none';
    preview.style.zIndex = '2147483647';

    const cells = [
      '::',
      row.series,
      row.status,
      row.patientId,
      row.patientName,
      row.studyId,
      row.startedAt,
    ];

    cells.forEach((text, index) => {
      const cell = document.createElement('div');
      cell.style.display = 'flex';
      cell.style.alignItems = 'center';
      cell.style.justifyContent = 'center';
      cell.style.height = '100%';
      cell.style.padding = index === 0 ? '0 4px' : '0 8px';
      cell.style.whiteSpace = 'nowrap';
      cell.textContent = text;
      preview.appendChild(cell);
    });

    document.body.appendChild(preview);
    return preview;
  };

  const clearDragState = () => {
    setDragRowId(null);
    setDragOverRowId(null);
    setShiftUpRowIds([]);
    setShiftDownRowIds([]);
    if (shiftResetTimerRef.current !== null) {
      window.clearTimeout(shiftResetTimerRef.current);
      shiftResetTimerRef.current = null;
    }
    clearDragImage();
  };

  useEffect(() => {
    rowsRef.current = rows;
  }, [rows]);

  useEffect(() => {
    if (!accessToken) {
      setRows([]);
      rowsRef.current = [];
      return;
    }

    let isMounted = true;
    const fetchJobQueues = async () => {
      const response = await getJobQueues({ accessToken });
      if (!isMounted || !response || response.status !== 200) {
        return;
      }

      const payload = (response.data as { data?: JobQueueApiItem[] } | undefined)?.data ?? [];
      const mappedRows = toJobQueueRows(payload);
      setRows(mappedRows);
      rowsRef.current = mappedRows;
    };

    fetchJobQueues();

    return () => {
      isMounted = false;
    };
  }, [accessToken]);

  useEffect(
    () => () => {
      clearDragImage();
      if (shiftResetTimerRef.current !== null) {
        window.clearTimeout(shiftResetTimerRef.current);
      }
    },
    [],
  );

  const applyRowShiftAnimation = (sourceRows: JobQueueRow[], fromIndex: number, toIndex: number) => {
    const upIds: string[] = [];
    const downIds: string[] = [];

    if (fromIndex < toIndex) {
      for (let i = fromIndex + 1; i <= toIndex; i += 1) {
        upIds.push(sourceRows[i].id);
      }
    } else {
      for (let i = toIndex; i < fromIndex; i += 1) {
        downIds.push(sourceRows[i].id);
      }
    }

    setShiftUpRowIds(upIds);
    setShiftDownRowIds(downIds);

    if (shiftResetTimerRef.current !== null) {
      window.clearTimeout(shiftResetTimerRef.current);
    }
    shiftResetTimerRef.current = window.setTimeout(() => {
      setShiftUpRowIds([]);
      setShiftDownRowIds([]);
      shiftResetTimerRef.current = null;
    }, 180);
  };

  const handleDragStart = (rowId: string) => (event: React.DragEvent<HTMLButtonElement>) => {
    const row = rows.find((item) => item.id === rowId);

    setDragRowId(rowId);
    setDragOverRowId(rowId);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', rowId);

    clearDragImage();
    if (row) {
      const preview = createDragImage(row);
      dragImageRef.current = preview;
      event.dataTransfer.setDragImage(preview, 24, 16);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (dragRowId === null) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnterRow = (targetRowId: string) => (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();

    if (
      dragRowId === null ||
      dragRowId === targetRowId ||
      dragOverRowId === targetRowId
    ) {
      return;
    }

    event.dataTransfer.dropEffect = 'move';

    const currentRows = rowsRef.current;
    const fromIndex = currentRows.findIndex((row) => row.id === dragRowId);
    const toIndex = currentRows.findIndex((row) => row.id === targetRowId);
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) {
      return;
    }

    setDragOverRowId(targetRowId);
    applyRowShiftAnimation(currentRows, fromIndex, toIndex);

    const nextRows = moveRow(currentRows, fromIndex, toIndex);
    rowsRef.current = nextRows;
    setRows(nextRows);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    clearDragState();
  };

  return (
    <div className={styles.content}>
      <div className={styles.form}>
        <div className={styles.formheader}>
          <div className={styles.header}>
            <span>Job Queue</span>
          </div>
          <div className={styles.divider} />
        </div>

        <div className={styles.block}>
          <div className={styles.blockinput}>
            <div className={styles.blockcontent}>
              <div className={`${styles.column} ${styles.columnGrip}`}>
                <div className={`${styles.item} ${styles.itemHead}`} />
                {rows.map((row) => (
                  <div
                    key={`grip-${row.id}`}
                    className={`${styles.item} ${styles.itemBody} ${styles.gripCell} ${
                      dragRowId === row.id ? styles.rowDragging : ''
                    } ${dragOverRowId === row.id ? styles.rowDropTarget : ''} ${
                      dragRowId === row.id ? styles.gripCellDragging : ''
                    } ${shiftUpRowIds.includes(row.id) ? styles.rowShiftUp : ''} ${
                      shiftDownRowIds.includes(row.id) ? styles.rowShiftDown : ''
                    }`}
                    onDragOver={handleDragOver}
                    onDragEnter={handleDragEnterRow(row.id)}
                    onDrop={handleDrop}
                  >
                    <button
                      type="button"
                      className={styles.dragHandle}
                      draggable
                      onDragStart={handleDragStart(row.id)}
                      onDragEnd={clearDragState}
                    >
                      <GDV className={styles.dragIcon} />
                    </button>
                  </div>
                ))}
              </div>

              {columns.map((column) => (
                <div key={column.key} className={styles.column}>
                  <div className={`${styles.item} ${styles.itemHead}`}>
                    <span className={styles.textHead}>{column.label}</span>
                  </div>
                  {rows.map((row) => (
                    <div
                      key={`${row.id}-${column.key}`}
                      className={`${styles.item} ${styles.itemBody} ${
                        dragRowId === row.id ? styles.rowDragging : ''
                      } ${dragOverRowId === row.id ? styles.rowDropTarget : ''} ${
                        shiftUpRowIds.includes(row.id) ? styles.rowShiftUp : ''
                      } ${shiftDownRowIds.includes(row.id) ? styles.rowShiftDown : ''}`}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnterRow(row.id)}
                      onDrop={handleDrop}
                    >
                      <span className={styles.textItem} title={row[column.key]}>
                        {row[column.key]}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default JobQueueSetting;
